import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: ObjectComponent
 * SubType: Configuration
 * Reason: ObjectComponent for ch.ChInboundSourceConfig. ChInboundSourceConfig is
 *         a Complex Configuration Entity (Complex Configuration Entity Rule /
 *         config-page-audit CONFIG-003/010): it has THREE same-module
 *         configurable children, each carrying an inboundSourceConfig FK:
 *           - ch.ChWhatsappNumberConfig
 *           - ch.ChMailboxConfig
 *           - ch.ChSmsConfig
 *         It is therefore rendered as a dedicated Object Page (NOT flat widgets
 *         with separate config-page nav items). The three children are exposed
 *         as left-navigation sections, each a table widget filtered by the
 *         selected parent (inboundSourceConfig = objectId). Child create actions
 *         default + read-only the inboundSourceConfig FK to the parent
 *         (CONFIG-012, widgetToolbarCreate fieldValue pattern).
 *
 *         Registered via ch.entity-provider.ts for MvsCrudModeEnum.update so a
 *         row-click on the "Eingangsquellen" config-page widget opens this
 *         Object Page instead of the generic CRUD sidebar (CONFIG-015 click
 *         chain via navigationService.navigateTo → 'object').
 *
 * @ticket TM-1000001922
 */
@Component({
    selector: 'mvs-ch-inbound-source-config-object',
    templateUrl: './ch-inbound-source-config-object.component.html',
    styleUrls: ['./ch-inbound-source-config-object.component.scss'],
    standalone: false,
})
export class ChInboundSourceConfigObjectComponent extends ObjectBaseComponent implements OnInit, OnDestroy {

    /** Child table widgets, each filtered by the selected parent source config. */
    whatsappNumbersWidget: WidgetData;
    mailboxesWidget: WidgetData;
    smsConfigsWidget: WidgetData;

    /** Left-navigation sections — one per same-module configurable child. */
    navigationItems: NavigationItem[] = [
        {
            label: 'WhatsApp-Nummern',
            action: 'whatsappNumbers',
            tooltip: 'WhatsApp-Nummern dieser Eingangsquelle',
            icon: 'fa-solid fa-comment-sms',
            toggleable: false,
            default: true,
        },
        {
            label: 'Postfächer',
            action: 'mailboxes',
            tooltip: 'E-Mail-Postfächer dieser Eingangsquelle',
            icon: 'fa-solid fa-envelope',
            toggleable: false,
            default: false,
        },
        {
            label: 'SMS-Konfiguration',
            action: 'smsConfigs',
            tooltip: 'SMS-Konfigurationen dieser Eingangsquelle',
            icon: 'fa-solid fa-mobile-screen-button',
            toggleable: false,
            default: false,
        },
    ];

    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        private route: ActivatedRoute,
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // ObjectBaseComponent does not subscribe to ?navItem= itself (G6.5):
        // wire the query param so deep-links / section switches select the
        // active left-nav section.
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];
            if (navItem) {
                const found = this.navigationItems.find(item => item.action === navItem);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    /**
     * Called when the parent ChInboundSourceConfig dto is loaded or refreshed.
     * Emits the left-navigation items to the Object Page host and (re)creates
     * the three child widgets filtered by the current source config id.
     */
    onObjectChanged(): void {
        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.onNavigationItems.emit(this.navigationItems);
        this.createChildWidgets();
    }

    private createChildWidgets(): void {
        const sourceConfigId = this.objectIdentifier.objectId;

        // Filter children by the parent FK. QL filter fields MUST be qualified
        // with the start-entity content reference ("e", fixed by
        // WidgetFactory.createQlWidget) AND use the FK "Id" suffix
        // (fe_core_ql FK naming rule) — the bare relationship name resolves to
        // neither a content reference nor a known attribute and throws
        // "Plan Generation Failed". All three child entities
        // (ChWhatsappNumberConfig, ChMailboxConfig, ChSmsConfig) expose the same
        // inboundSourceConfig FK, so e.inboundSourceConfigId is correct for all.
        const filter = [
            FilterCriteria.create('e.inboundSourceConfigId', FilterCriteria.cOperatorEqual, sourceConfigId),
        ];

        // CONFIG-012: pre-fill + read-only the parent FK in the child create form.
        const fkFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'inboundSourceConfigDtoId', fieldValue: sourceConfigId},
        ];

        /**
         * AI:
         * Status: 'wave-3-implementation'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: ChWhatsappNumberConfig children filtered by parent source config.
         */
        this.whatsappNumbersWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'ch.inbound.source.config.children.whatsapp',
            'WhatsApp-Nummern',
            'ch.ChWhatsappNumberConfig',
            filter,
        );
        this.whatsappNumbersWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);

        /**
         * AI:
         * Status: 'wave-3-implementation'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: ChMailboxConfig children filtered by parent source config.
         */
        this.mailboxesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'ch.inbound.source.config.children.mailbox',
            'Postfächer',
            'ch.ChMailboxConfig',
            filter,
        );
        this.mailboxesWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);

        /**
         * AI:
         * Status: 'wave-3-implementation'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: ChSmsConfig children filtered by parent source config.
         */
        this.smsConfigsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'ch.inbound.source.config.children.sms',
            'SMS-Konfiguration',
            'ch.ChSmsConfig',
            filter,
        );
        this.smsConfigsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.queryParamSubscription?.unsubscribe();
    }
}
